import { useCallback, useState } from 'react';
import type { ColorBalanceTriple, ColorBalanceValue } from '../../lib/adjustments/color-balance';
import type { AdjustmentPanelProps } from './registry';

type TonalRange = 'shadows' | 'midtones' | 'highlights';

const TONAL_RANGES: { value: TonalRange; label: string }[] = [
  { value: 'shadows', label: 'Shadows' },
  { value: 'midtones', label: 'Midtones' },
  { value: 'highlights', label: 'Highlights' },
];

const AXIS_LABELS: { left: string; right: string }[] = [
  { left: 'Cyan', right: 'Red' },
  { left: 'Magenta', right: 'Green' },
  { left: 'Yellow', right: 'Blue' },
];

export function ColorBalancePanel({ value, onChange }: AdjustmentPanelProps<ColorBalanceValue>) {
  const [activeRange, setActiveRange] = useState<TonalRange>('midtones');

  const triple = value[activeRange];

  const updateAxis = useCallback(
    (axis: 0 | 1 | 2, next: number) => {
      const updated: ColorBalanceTriple = [...triple];
      updated[axis] = next;
      onChange({ ...value, [activeRange]: updated });
    },
    [activeRange, onChange, triple, value],
  );

  const resetActiveRange = useCallback(() => {
    onChange({ ...value, [activeRange]: [0, 0, 0] as ColorBalanceTriple });
  }, [activeRange, onChange, value]);

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {TONAL_RANGES.map((r) => (
          <button
            key={r.value}
            type="button"
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              activeRange === r.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => setActiveRange(r.value)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {AXIS_LABELS.map((axis, i) => (
          <BalanceRow
            key={axis.left}
            labelLeft={axis.left}
            labelRight={axis.right}
            value={triple[i as 0 | 1 | 2]}
            onChange={(v) => updateAxis(i as 0 | 1 | 2, v)}
          />
        ))}
      </div>

      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={value.preserveLuminosity}
          onChange={(e) => onChange({ ...value, preserveLuminosity: e.target.checked })}
          className="h-3.5 w-3.5"
        />
        Preserve Luminosity
      </label>

      <button
        type="button"
        className="text-[11px] text-muted-foreground hover:text-foreground"
        onClick={resetActiveRange}
      >
        Reset {TONAL_RANGES.find((r) => r.value === activeRange)?.label}
      </button>
    </div>
  );
}

function BalanceRow({
  labelLeft,
  labelRight,
  value,
  onChange,
}: {
  labelLeft: string;
  labelRight: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{labelLeft}</span>
        <span>{labelRight}</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={-100}
          max={100}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1"
        />
        <input
          type="number"
          min={-100}
          max={100}
          step={1}
          value={Math.round(value)}
          onChange={(e) => {
            const n = Number(e.target.value);
            // Clamp typed values to the slider range — see RangeRow.
            if (Number.isFinite(n)) onChange(n < -100 ? -100 : n > 100 ? 100 : n);
          }}
          className="w-14 text-xs text-right tabular-nums bg-muted rounded px-1 py-0.5 border border-border"
        />
      </div>
    </div>
  );
}
